import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { FORCE_PROMOTE_KEYS, PINNED_NAV_CAP } from '../../utils/productNav';
import { isFeatureUsable } from '../../utils/productLicensing';
import './CustomizeNavDialog.css';

const pinCapFor = (availableKeys) => {
  const total = (availableKeys || []).length;
  if (!total) {
    return PINNED_NAV_CAP;
  }
  return Math.min(PINNED_NAV_CAP, total);
};

const withForcedPins = (keys, availableKeys) => {
  const next = (keys || []).filter((key) => availableKeys.indexOf(key) !== -1);
  FORCE_PROMOTE_KEYS.forEach((forceKey) => {
    if (availableKeys.indexOf(forceKey) === -1) {
      return;
    }
    if (next.indexOf(forceKey) === -1) {
      next.unshift(forceKey);
    }
  });
  return next.slice(0, pinCapFor(availableKeys));
};

const CustomizeNavDialog = ({
  open,
  onClose,
  onSave,
  rankedProducts,
  pinnedKeys,
  t,
}) => {
  const availableKeys = (rankedProducts || []).map(
    (entry) => entry.product.feature_key
  );
  const pinCap = pinCapFor(availableKeys);
  const [draft, setDraft] = useState(() =>
    withForcedPins(pinnedKeys, availableKeys)
  );
  const [error, setError] = useState('');
  const [dragKey, setDragKey] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    const keys = (rankedProducts || []).map(
      (entry) => entry.product.feature_key
    );
    setDraft(withForcedPins(pinnedKeys, keys));
    setError('');
  }, [open]);

  const labelFor = (key) => {
    const match = (rankedProducts || []).find(
      (entry) => entry.product.feature_key === key
    );
    return match
      ? t(match.product.labelKey, { defaultValue: match.product.defaultLabel })
      : key;
  };

  const isLocked = (key) => {
    const match = (rankedProducts || []).find(
      (entry) => entry.product.feature_key === key
    );
    return !isFeatureUsable(match && match.entitlement);
  };

  const isForced = (key) => FORCE_PROMOTE_KEYS.indexOf(key) !== -1;

  const handleRemove = (key) => {
    if (isForced(key)) {
      setError(
        t('NavCustomize.ForcePromote', {
          defaultValue: 'Marketplace stays pinned so staff can find it.',
        })
      );
      return;
    }
    setDraft(draft.filter((item) => item !== key));
    setError('');
  };

  const handleAdd = (key) => {
    if (draft.indexOf(key) !== -1) {
      return;
    }
    if (draft.length >= pinCap) {
      setError(
        t('NavCustomize.PinLimit', {
          defaultValue: 'You can pin up to {{count}} items.',
          count: pinCap,
        })
      );
      return;
    }
    setDraft(withForcedPins(draft.concat(key), availableKeys));
    setError('');
  };

  const handleDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) {
      return;
    }
    const without = draft.filter((key) => key !== dragKey);
    const index = without.indexOf(targetKey);
    without.splice(index, 0, dragKey);
    setDraft(withForcedPins(without, availableKeys));
    setDragKey('');
  };

  const available = availableKeys.filter((key) => draft.indexOf(key) === -1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="customize-nav-dialog"
    >
      <DialogTitle className="customize-nav-title">
        <span>
          {t('NavCustomize.Title', { defaultValue: 'Customize navigation' })}
        </span>
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p className="customize-nav-copy">
          {t('NavCustomize.Copy', {
            defaultValue:
              'Only pinned products appear in the left menu. Drag to reorder. Pin a locked product if staff should still be able to request it.',
            count: pinCap,
          })}
        </p>
        <div className="customize-nav-grid">
          <section>
            <h3>
              {t('NavCustomize.Pinned', { defaultValue: 'Pinned' })}
              <span>
                {draft.length} / {pinCap}
              </span>
            </h3>
            {draft.map((key) => (
              <div
                key={key}
                className="customize-nav-row"
                draggable
                onDragStart={() => setDragKey(key)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => handleDrop(key)}
              >
                <DragIndicatorIcon className="customize-nav-drag" />
                <span>{labelFor(key)}</span>
                {isLocked(key) ? (
                  <LockOutlinedIcon className="customize-nav-lock" />
                ) : null}
                <IconButton
                  size="small"
                  onClick={() => handleRemove(key)}
                  aria-label="unpin"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </section>
          <section>
            <h3>{t('NavCustomize.Available', { defaultValue: 'Available' })}</h3>
            {available.map((key) => (
              <div key={key} className="customize-nav-row">
                <span>{labelFor(key)}</span>
                {isLocked(key) ? (
                  <LockOutlinedIcon className="customize-nav-lock" />
                ) : null}
                <IconButton
                  size="small"
                  onClick={() => handleAdd(key)}
                  aria-label="pin"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </section>
        </div>
        {error ? <p className="customize-nav-error">{error}</p> : null}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onSave(null);
            onClose();
          }}
        >
          {t('NavCustomize.Reset', { defaultValue: 'Reset to defaults' })}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(withForcedPins(draft, availableKeys));
            onClose();
          }}
        >
          {t('NavCustomize.Save', { defaultValue: 'Save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CustomizeNavDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  rankedProducts: PropTypes.array,
  pinnedKeys: PropTypes.array,
  t: PropTypes.func.isRequired,
};

CustomizeNavDialog.defaultProps = {
  open: false,
  rankedProducts: [],
  pinnedKeys: [],
};

export default CustomizeNavDialog;



// WEBPACK FOOTER //
// ./src/components/leftnav/CustomizeNavDialog.js